import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Checkout } from 'src/app/state/user/checkouts/models/checkout';
import { ApiService } from 'src/app/services/api.service';
import { Icon, Record } from 'src/app/models/record';
import { ButtonsConfiguration, CheckoutItemButtonConfiguration } from 'src/app/models/buttons-config';
import { LocaleService } from 'src/app/services/locale.service';
import { DatePipe } from '@angular/common';
import { Util } from 'src/app/util';
import { ConfigService } from '../../../services/config.service';
import moment from 'moment';
import { LoggerService } from 'src/app/services/logger.service';
import { RssItem } from 'src/app/models/rss';
import { CardItemCover, CardItemProgress } from '../../../_shared/components/card-item/card-item.interface';
import { ToastService } from '../../../_core/services/toast.service';

@Component({
  selector: 'app-user-checkout-item',
  templateUrl: './user-checkout-item.component.html',
  styleUrls: ['./user-checkout-item.component.scss'],
})
export class UserCheckoutItemComponent implements OnInit, OnChanges {
  private readonly toastService = inject(ToastService);

  @Input()
  checkout: Checkout;

  @Input()
  userId: string;

  record: Record;
  rssItem: RssItem;
  slug: string;
  timeLeft: string;
  buttonConfig: ButtonsConfiguration;

  cover: string;
  title: string;
  icon: Icon;
  loaded: boolean;

  coverDetails?: CardItemCover = undefined;
  cardLink: string = '';
  showExpireDate: boolean = false;

  cardProgress?: CardItemProgress = undefined;

  constructor(
    private apiService: ApiService,
    private configService: ConfigService,
    private localeService: LocaleService,
    private logger: LoggerService,
    private datePipe: DatePipe,
  ) {
    this.buttonConfig = new CheckoutItemButtonConfiguration(this.configService.get().showReturnLoanButton);
  }

  ngOnInit() {
    this.showExpireDate = this.configService.get().showExpireDateCollectionItem !== false;

    this.calculateExpiringTime();
    this.apiService.getRecord(this.checkout.recordId, true).subscribe({
      next: (record) => {
        this.record = record;
        this.slug = Util.slugifyRecord(record);

        this.cardLink = `/info/${this.slug}`;
        if (this.checkout.rssRhId) {
          this.cardLink += `?rhId=${this.checkout.rssRhId}`;
        }

        if (this.checkout.percentage) {
          this.cardProgress = {
            percentage: Math.trunc(this.checkout.percentage),
          }
        }

        if (record.container) {
          this.apiService.getRssItem(record.id, this.checkout.rssRhId).subscribe(
            (rssItem) => {
              this.rssItem = rssItem;
              this.setRssItemData(rssItem);
              this.loaded = true;
            },
            (_) => {
              this.logger.error(`Error fetching rssItem with rhId: ${this.checkout.rssRhId} for record ${record.id}.`);
              this.setRecordData(record);
              this.loaded = true;
            }
          );
        } else {
          this.setRecordData(record);
          this.loaded = true;
        }
      },
      error: (error) => {
        this.loaded = true;
        this.logger.error(error);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.checkout) {
      if (changes.checkout.currentValue.percentage) {
        this.cardProgress = {
          percentage: Math.trunc(changes.checkout.currentValue.percentage),
        }
      }
    }
  }

  onRenewal(renewal: Checkout) {
    this.checkout.renewed = true;
    this.calculateExpiringTime();

    const dateString =
      this.datePipe.transform(this.checkout.endTime) +
      ' - ' +
      this.datePipe.transform(this.checkout.endTime, 'shortTime');

    this.toastService.showToastSolution('messages.checkouts.renew', 'success', { date: dateString });
  }

  private calculateExpiringTime() {
    const realEndTime = moment(Util.getRealEndTime(this.checkout));
    const diff = moment.duration(moment(realEndTime).diff(moment()));

    this.timeLeft = this.localeService.humanize(diff.asMilliseconds());
    this.checkout.endTime = String(realEndTime.valueOf());
  }

  private setRecordData(record: Record) {
    this.title = record.title;

    this.coverDetails = {
      url: record.coverImageUrl,
      icon: record.icon,
      isList: Boolean(record.wishListInfo),
    };
  }

  private setRssItemData(rssItem: RssItem) {
    Util.prepareRssItem(rssItem);
    this.title = rssItem.title;

    this.coverDetails = {
      url: rssItem.cover,
      icon: rssItem.icon,
      isList: Boolean(this.record.wishListInfo),
    }
  }
}
